import type { SRT_TableOptions } from '@/shared/components/master-table'
import type { ScenarioRunSetRow } from '../model/types'

import { useCallback } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { ShareIcon, Trash2Icon } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { useCopyToClipboard } from 'usehooks-ts'

import { useDeleteScenarioRunSetMutation } from '@/entities/scenario'

import { confirm } from '@/shared/components/ui/confirmer'
import { DropdownMenuItem } from '@/shared/components/ui/dropdown-menu'
import { ampli } from '@/shared/lib/ampli'

type Props = Parameters<
	NonNullable<SRT_TableOptions<ScenarioRunSetRow>['renderRowActionMenuItems']>
>
export const useWhatifRunsRowAction = () => {
	const { t } = useTranslation()

	const queryClient = useQueryClient()
	const [, copyToClipboard] = useCopyToClipboard()
	const deleteScenarioRunsMutation = useDeleteScenarioRunSetMutation({
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: ['scenarios-run-sets'],
			})
		},
	})

	return useCallback(
		({ row }: Props[0]) => [
			<DropdownMenuItem
				key='share'
				onClick={() => {
					ampli.whatIfRunsSetActionClicked({
						runs_set_action: 'share',
					})
					copyToClipboard(
						`${window.location.host}/p/whatif/runs/${row.original.id}/summary`
					)
						.then(() => {
							toast.success(
								t('action.share_success', {
									defaultValue: 'Link copied to clipboard',
								})
							)
						})
						.catch(() => {
							toast.error(
								t('action.share_error', { defaultValue: 'Failed to copy link' })
							)
						})
				}}
			>
				<ShareIcon /> {t('action.share', { defaultValue: 'Share' })}
			</DropdownMenuItem>,
			<DropdownMenuItem
				onClick={() => {
					ampli.whatIfRunsSetActionClicked({
						runs_set_action: 'delete',
					})
					confirm({
						title: t('general_delete_confirm_title', {
							defaultValue: 'Delete',
						}),
						description: t('general_delete_confirm_text', {
							defaultValue: 'Are you sure you want to delete this item?',
						}),
						actionText: t('general_delete', {
							ns: 'keys',
						}),
						cancelText: t('general_cancel', {
							ns: 'keys',
						}),
						ActionProps: {
							variant: 'destructive',
						},
					}).then((answer: boolean) => {
						if (answer) {
							toast.promise(
								deleteScenarioRunsMutation.mutateAsync({
									scenarioRunSetId: row.original.id,
								}),
								{
									loading: t('deleting.proccess', {
										defaultValue: 'Deleting...',
									}),
									success: t('deleting.success', {
										defaultValue: 'Deleted successfully',
									}),
									error: t('deleting.error', {
										defaultValue: 'Error while deleting',
									}),
								}
							)
						}
					})
				}}
				key='delete'
				variant='destructive'
			>
				<Trash2Icon /> {t('action.delete', { defaultValue: 'Delete' })}
			</DropdownMenuItem>,
		],
		[deleteScenarioRunsMutation, t, copyToClipboard]
	)
}
